import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, throwIfError } from "../client";
import { eventKeys } from "../query-keys";
import type { EventId } from "@/types/identifiers";
import type { components } from "@/api/generated/schema";

export { eventKeys } from "../query-keys";

type EventCreate = components["schemas"]["EventCreate"];
type EventUpdate = components["schemas"]["EventUpdate"];

export function useEvents(season?: number) {
  return useQuery({
    queryKey: eventKeys.list(season),
    queryFn: async () => {
      const result = await api.GET("/api/events", {
        params: { query: season ? { season } : undefined },
      });
      return throwIfError(result, "Failed to fetch events");
    },
  });
}

export function useEvent(id: EventId) {
  return useQuery({
    queryKey: eventKeys.detail(id),
    queryFn: async () => {
      const result = await api.GET("/api/events/{event_id}", {
        params: { path: { event_id: id } },
      });
      return throwIfError(result, "Failed to fetch event");
    },
    enabled: !!id,
  });
}

export function useCreateEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: EventCreate) => {
      const result = await api.POST("/api/events", { body });
      return throwIfError(result, "Failed to create event");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: eventKeys.all });
    },
  });
}

export function useUpdateEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: EventId; data: EventUpdate }) => {
      const result = await api.PATCH("/api/events/{event_id}", {
        params: { path: { event_id: id } },
        body: data,
      });
      return throwIfError(result, "Failed to update event");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: eventKeys.all });
    },
  });
}
